/* eslint-disable */
import React, { useMemo, useEffect } from 'react';
import * as lib from '~/dependencies';

interface CustomIconComponents {
  ValidIcon: React.ReactNode;
  InvalidIcon: React.ReactNode;
}

interface PasswordProps {
  value: string;
  confirmPassword?: string;
  currentPassword?: string;
  minLength?: number;
  maxLength?: number;
  iconSize?: number;
  validColor?: string;
  invalidColor?: string;
  validTextColor?: string;
  invalidTextColor?: string;
  onChange?: (isValid: boolean, failedRules: RuleNames[]) => any;
  messages?: {
    [key in RuleNames]?: string;
  };
  iconComponents?: CustomIconComponents;
}

export type RuleNames =
  | 'minLength'
  | 'maxLength'
  | 'mustIncludeSymbols'
  | 'mustIncludeNumbers'
  | 'match'
  | 'notEmpty'
  | 'mustMixedCase'
  | 'noSpaces'
  | 'noReuse';

export interface PasswordChecklistProps extends PasswordProps {
  className?: string;
  style?: React.CSSProperties;
  rules: Array<RuleNames>;
  hideIcon?: boolean;
  specialCharsRegex?: RegExp;
  itemClassName?: string;
}

const valueHasCapitalCharacters = (value: string) => {
  if (value.length === 0) return false;

  let i = 0;

  while (i < value.length) {
    const character = value.charAt(i);
    // Check if the character is a letter
    if (/[a-zA-Z]/.test(character)) {
      if (character == character.toUpperCase()) {
        return true;
      }
    }
    i++;
  }

  return false;
};

const valueHasLowercaseCharacters = (value: string) => {
  if (value.length === 0) return false;

  let i = 0;

  while (i < value.length) {
    const character = value.charAt(i);

    // Check if the character is a letter
    if (/[a-zA-Z]/.test(character)) {
      if (character == character.toLowerCase()) {
        return true;
      }
    }
    i++;
  }

  return false;
};

const PasswordChecklist: React.FC<PasswordChecklistProps> = ({
  className,
  style,
  rules,
  value,
  confirmPassword,
  currentPassword,
  minLength = 8,
  maxLength = 32,
  onChange,
  specialCharsRegex = /[~`¿¡!#$%\^&*€£@+÷=\-\[\]\\';,/{}\(\)|\\":<>\?\.\_]/g,
  messages = {},
  ...remainingProps
}) => {
  const { t } = lib.useTranslation();

  const ruleDefinitions: {
    [key in RuleNames]: { valid: boolean; message: string };
  } = {
    minLength: {
      valid: value.length >= minLength,
      message:
        messages.minLength ||
        t('screens.auth.redefinePassword.validator.passwordMinLength', {
          value: minLength,
        }),
    },
    maxLength: {
      valid: value.length <= maxLength,
      message:
        messages.maxLength ||
        t('screens.auth.redefinePassword.validator.passwordMinLength', {
          value: maxLength,
        }),
    },
    mustIncludeSymbols: {
      valid: specialCharsRegex.test(value),
      message:
        messages.mustIncludeSymbols ||
        t('screens.auth.redefinePassword.validator.passwordMustIncludeSymbols'),
    },
    // passwordMustIncludeNumbers
    mustIncludeNumbers: {
      valid: /\d/g.test(value),
      message:
        messages.mustIncludeNumbers ||
        t('screens.auth.redefinePassword.validator.passwordMustIncludeNumbers'),
    },
    mustMixedCase: {
      valid:
        valueHasCapitalCharacters(value) && valueHasLowercaseCharacters(value),
      message:
        messages.mustMixedCase ||
        t('screens.auth.redefinePassword.validator.passwordMustMixedCase'),
    },
    match: {
      valid: value.length > 0 && value === confirmPassword,
      message:
        messages.match ||
        t('screens.auth.redefinePassword.validator.passwordMatch'),
    },
    notEmpty: {
      valid: Boolean(
        value.length > 0 && confirmPassword && confirmPassword.length > 0
      ),
      message: messages.notEmpty || 'Password fields are not empty.',
    },
    noSpaces: {
      valid: !value.includes(' '),
      message: messages.noSpaces || 'Password contains no spaces.',
    },
    noReuse: {
      valid: value === currentPassword,
      message:
        messages.match ||
        t('screens.auth.redefinePassword.validator.passwordNoReuse'),
    },
  };

  const enabledRules: RuleNames[] = useMemo(
    () => rules.filter(rule => Boolean(ruleDefinitions[rule])),
    [rules]
  );
  const isValid: boolean = enabledRules.every(
    rule => ruleDefinitions[rule].valid
  );
  const failedRules: RuleNames[] = useMemo(
    () => enabledRules.filter(rule => !ruleDefinitions[rule].valid),
    [value, confirmPassword, enabledRules]
  );

  useEffect(() => {
    if (typeof onChange === 'function') {
      onChange(isValid, failedRules);
    }
  }, [isValid, failedRules]);

  return (
    <ul
      className={className}
      style={{
        margin: 0,
        padding: 0,
        width: '100%',
        ...style,
      }}>
      {enabledRules.map(rule => {
        const { message, valid } = ruleDefinitions[rule];
        return (
          <Rule
            key={rule}
            valid={valid}
            iconSize={18}
            validColor="#4BCA81"
            invalidColor="#FF0033"
            {...remainingProps}>
            {message}
          </Rule>
        );
      })}
    </ul>
  );
};

interface RuleProps {
  valid: boolean;
  iconSize?: number;
  iconComponents?: CustomIconComponents;
  validColor?: string;
  invalidColor?: string;
  validTextColor?: string;
  invalidTextColor?: string;
  hideIcon?: boolean;
  children?: React.ReactNode;
  itemClassName?: string;
}

const Rule: React.FC<RuleProps> = ({
  valid,
  iconSize,
  validColor,
  invalidColor,
  validTextColor,
  invalidTextColor,
  iconComponents,
  hideIcon,
  children,
  itemClassName,
}) => {
  return (
    <li
      className={`${itemClassName} ${valid ? 'valid' : 'invalid'}`}
      style={{
        listStyleType: 'none',
        display: 'flex',
        alignItems: 'center',
        margin: '2px 0',
      }}>
      {!hideIcon ? (
        iconComponents ? (
          valid ? (
            iconComponents.ValidIcon
          ) : (
            iconComponents.InvalidIcon
          )
        ) : (
          <svg
            className="checklist-icon"
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
            width={iconSize}
            height={iconSize}
            viewBox="0 0 512 512"
            style={{ marginRight: 10, marginLeft: 0 }}>
            <path
              fill={valid ? validColor : invalidColor}
              d={valid ? 'M432 64l-240 240-112-112-80 80 192 192 320-320z' : ''}
            />
          </svg>
        )
      ) : null}
      <span
        style={{
          paddingTop: 2,
          opacity: valid ? 1 : !invalidTextColor ? 0.5 : undefined,
          flex: 1,
          color: valid ? validTextColor : invalidTextColor,
        }}>
        {children}
      </span>
    </li>
  );
};

export default PasswordChecklist;
